import { useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './context/AuthContext'
import Header from './components/Header'
import Sidebar from './components/Sidebar'
import Login from './components/Login'

// Video Library Modules
import StreamManager from './modules/video-library/stream-manager'
import StreamEditor from './modules/video-library/stream-editor'
import ArchiveManager from './modules/video-library/archive-manager'
import ImageManager from './modules/video-library/image-manager'
import MuxStreamManager from './modules/video-library/mux-stream-manager'
import MuxStreamEditor from './modules/video-library/mux-stream-editor'

// OnDemand Library Modules
import ContentCatalog from './modules/ondemand-library/content-catalog'
import Collections from './modules/ondemand-library/collections'
import TbtvHighlights from './modules/ondemand-library/tbtv-highlights'
import TbtvTeamsData from './modules/ondemand-library/tbtv-teams-data'
import TickerData from './modules/ondemand-library/ticker-data'

// Sources Modules
import YouTubeArchive from './modules/video-library/youtube-archive'
import TBTVChannels from './modules/sources/tbtv-channels'
import LiveInputs from './modules/sources/live-inputs'
import StrimPartners from './modules/sources/tbtv-strimm'

// Live Stream Modules
import LiveSchedule from './modules/live-stream/live-schedule'
import PlaylistBuilder from './modules/live-stream/playlist-builder'
import SSAIManager from './modules/live-stream/ssai-manager'
import Streamer from './modules/live-stream/streamer'
import TeamScheduler from './modules/live-stream/team-scheduler'

// Sponsors Modules
import SponsorManager from './modules/sponsors/sponsor-manager'
import ZonesList from './modules/sponsors/sponsor-manager/zones/ZonesList'
import ZoneDetail from './modules/sponsors/sponsor-manager/zones/ZoneDetail'
import AdsList from './modules/sponsors/sponsor-manager/ads/AdsList'
import AdDetail from './modules/sponsors/sponsor-manager/ads/AdDetail'
import GroupsList from './modules/sponsors/sponsor-manager/groups/GroupsList'
import GroupDetail from './modules/sponsors/sponsor-manager/groups/GroupDetail'
import AdvertisersList from './modules/sponsors/sponsor-manager/advertisers/AdvertisersList'
import AdvertiserDetail from './modules/sponsors/sponsor-manager/advertisers/AdvertiserDetail'
import CampaignsList from './modules/sponsors/sponsor-manager/campaigns/CampaignsList'
import AdBuilder, { AdBuilderEditor } from './modules/sponsors/ad-builder'
import SponsorStore from './modules/sponsors/sponsor-store/SponsorStore'

// Settings Modules
import SiteSettings from './modules/settings/SiteSettings'
import VodSettings from './modules/settings/VodSettings'
import LiveSettings from './modules/settings/LiveSettings'
import PlayerSettings from './modules/settings/PlayerSettings'
import YouTubeSettings from './modules/settings/YouTubeSettings'
import RokuSettings from './modules/settings/RokuSettings'
import AppleMobileSettings from './modules/settings/AppleMobileSettings'

// Placeholder components for future modules
function PlaceholderPage({ title, description }) {
  return (
    <div className="card">
      <h2 style={{ marginBottom: 12 }}>{title}</h2>
      <p className="muted">{description || 'This module is coming soon.'}</p>
    </div>
  )
}

// Dashboard home
function Dashboard() {
  return (
    <div>
      <h1 style={{ marginBottom: 20 }}>Welcome to TBTV Vader</h1>
      <div className="card">
        <h3 style={{ marginBottom: 12 }}>Quick Start</h3>
        <p className="muted">
          Select a module from the sidebar or use the top navigation to access different sections.
        </p>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 20 }}>
        <div className="card">
          <h4 style={{ marginBottom: 8 }}>Video Library</h4>
          <p className="muted">Manage your Cloudflare Stream video library, upload new content, and create clips.</p>
        </div>
        <div className="card">
          <h4 style={{ marginBottom: 8 }}>On-Demand Library</h4>
          <p className="muted">Organize your on-demand content catalog and collections.</p>
        </div>
        <div className="card">
          <h4 style={{ marginBottom: 8 }}>Live Stream</h4>
          <p className="muted">Manage live streaming schedules and EPG configuration.</p>
        </div>
        <div className="card">
          <h4 style={{ marginBottom: 8 }}>Analytics</h4>
          <p className="muted">View performance metrics across all platforms.</p>
        </div>
      </div>
    </div>
  )
}

export default function App() {
  const { isAuthenticated, loading } = useAuth()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  // Show loading state
  if (loading) {
    return (
      <div className="login-screen">
        <div style={{ textAlign: 'center' }}>
          <div className="login-logo">
            <span className="logo-text">TBTV</span>
            <span className="logo-vader">VADER</span>
          </div>
          <p className="muted">Loading...</p>
        </div>
      </div>
    )
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login />
  }

  return (
    <div className="app-layout">
      <Header 
        onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        sidebarCollapsed={sidebarCollapsed}
      />
      
      <div className="main-content">
        <Sidebar collapsed={sidebarCollapsed} />
        
        <main className={`page-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Routes>
            {/* Home */}
            <Route path="/" element={<Dashboard />} />
            
            {/* Video Library */}
            <Route path="/video-library" element={<Navigate to="/video-library/stream-manager" replace />} />
            <Route path="/video-library/stream-manager" element={<StreamManager />} />
            <Route path="/video-library/stream-editor" element={<StreamEditor />} />
            <Route path="/video-library/archive-manager" element={<ArchiveManager />} />
            <Route path="/video-library/youtube-archive" element={<YouTubeArchive />} />
            <Route path="/video-library/image-manager" element={<ImageManager />} />
            <Route path="/video-library/mux-stream-manager" element={<MuxStreamManager />} />
            <Route path="/video-library/mux-stream-editor" element={<MuxStreamEditor />} />
            <Route path="/video-library/bulk-upload" element={
              <PlaceholderPage title="Bulk Upload" description="Upload multiple videos at once." />
            } />
            
            {/* On-Demand */}
            <Route path="/ondemand" element={<Navigate to="/ondemand/catalog" replace />} />
            <Route path="/ondemand/catalog" element={<ContentCatalog />} />
            <Route path="/ondemand/collections" element={<Collections />} />
            <Route path="/ondemand/highlights" element={<TbtvHighlights />} />
            <Route path="/ondemand/teams-data" element={<TbtvTeamsData />} />
            <Route path="/ondemand/ticker-data" element={<TickerData />} />

            {/* Sources */}
            <Route path="/sources" element={<Navigate to="/sources/tbtv-channels" replace />} />
            <Route path="/sources/tbtv-channels" element={<TBTVChannels />} />
            <Route path="/sources/live-inputs" element={<LiveInputs />} />
            <Route path="/sources/tbtv-strimm" element={<StrimPartners />} />
            
            {/* Live Stream */}
            <Route path="/livestream" element={<Navigate to="/livestream/schedule" replace />} />
            <Route path="/livestream/schedule" element={<LiveSchedule />} />
            <Route path="/livestream/playlist-builder" element={<PlaylistBuilder />} />
            <Route path="/livestream/ssai-manager" element={<SSAIManager />} />
            <Route path="/livestream/streamer" element={<Streamer />} />
            <Route path="/livestream/team-scheduler" element={<TeamScheduler />} />
            <Route path="/livestream/pihl-scheduler" element={<Navigate to="/livestream/team-scheduler" replace />} />
            <Route path="/livestream/epg" element={
              <PlaceholderPage title="EPG Manager" description="Electronic Program Guide management." />
            } />
            <Route path="/livestream/live-now" element={
              <PlaceholderPage title="Live Now" description="Monitor current live streams." />
            } />
            
            {/* Sponsors (AdSpeed) */}
            <Route path="/sponsors" element={<Navigate to="/sponsors/dashboard" replace />} />
            <Route path="/sponsors/dashboard" element={<SponsorManager />} />
            <Route path="/sponsors/zones" element={<ZonesList />} />
            <Route path="/sponsors/zones/:id" element={<ZoneDetail />} />
            <Route path="/sponsors/ads" element={<AdsList />} />
            <Route path="/sponsors/ads/:id" element={<AdDetail />} />
            <Route path="/sponsors/groups" element={<GroupsList />} />
            <Route path="/sponsors/groups/:id" element={<GroupDetail />} />
            <Route path="/sponsors/advertisers" element={<AdvertisersList />} />
            <Route path="/sponsors/advertisers/:id" element={<AdvertiserDetail />} />
            <Route path="/sponsors/campaigns" element={<CampaignsList />} />
            <Route path="/sponsors/ad-builder" element={<AdBuilder />} />
            <Route path="/sponsors/ad-builder/new" element={<AdBuilderEditor />} />
            <Route path="/sponsors/ad-builder/:slug" element={<AdBuilderEditor />} />
            <Route path="/sponsors/store" element={<SponsorStore />} />
            {/* Placements + Packages are tabs inside Sponsor Store; these deep-link to them. */}
            <Route path="/sponsors/placements" element={<SponsorStore initialTab="placements" />} />
            <Route path="/sponsors/packages" element={<SponsorStore initialTab="packages" />} />

            {/* Analytics - External link, no routes needed */}

            {/* Settings */}
            <Route path="/settings" element={<Navigate to="/settings/site-settings" replace />} />
            <Route path="/settings/site-settings" element={<SiteSettings />} />
            <Route path="/settings/youtube" element={<YouTubeSettings />} />
            <Route path="/settings/vod" element={<VodSettings />} />
            <Route path="/settings/live" element={<LiveSettings />} />
            <Route path="/settings/player" element={<PlayerSettings />} />
            <Route path="/settings/roku" element={<RokuSettings />} />
            <Route path="/settings/apple" element={<AppleMobileSettings />} />
            <Route path="/settings/api-keys" element={
              <PlaceholderPage title="API Keys" description="Manage API credentials and integrations." />
            } />
            
            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}
